/**
 * Centralized API Endpoints Configuration
 * 
 * This file contains all API endpoint paths used throughout the application.
 * This ensures consistency, maintainability, and makes it easy to update endpoints.
 * 
 * @module API Configuration
 */

/**
 * API endpoint paths
 */
export const API_ENDPOINTS = {
  // Authentication endpoints
  LOGIN: '/login',
  REGISTER: '/register',
  LOGOUT: '/api/logout',
  /** Keeps server session warm when UI is active but APIs are quiet (Angular LayoutComponent). */
  BEACON: '/api/beacon',
  CHANGE_ROLE: '/api/change_role',

  // User endpoints
  USER: '/user',
  CURRENT_USER: '/user',
  VERIFY_USER: '/api/verify',
  PROFILE: '/api/profile',
  PASSWORD: '/api/password',

  // Application endpoints
  RESIDENT_APPLICATION: '/api/resident_application',
  RESIDENT_APPLICATION_BY_ID: (id: string | number) => `/api/resident_application/${id}`,
  PREVIOUS_APPLICATION: '/api/previous_application',
  UPDATE_USER_INVITE: '/api/update_user_invite',
  APPLICATION_LIST: '/api/application',
  APPLICATION_DETAIL: (id: string | number) => `/api/application/${id}`,
  APPLICATION_DENIAL_REASONS: (id: string | number) => `/api/applicants/${id}/denied-email-content`,
  UPDATE_APPLICANT_STATUS: (id: string | number) => `/api/update_applicant_status/${id}`,
  TRANSLATE_APPLICATION: (id: string | number) => `/api/translate_application/${id}`,
  RESET_APPLICATION: (id: string | number) => `/api/reset_application/${id}`,
  SWAP_APPLICANT: (id: string | number) => `/api/swap_applicant/${id}`,
  CHECKLIST_TEMPLATE: '/api/config_applicant_checklist',
  SAVE_CHECKLIST: (id: string | number) => `/api/save_applicant_checklist/${id}`,
  IMPERSONATE: '/api/impersonate',

  // Dashboard endpoints
  DASHBOARD_TILES: '/api/dashboard_tiles',
  ABOUT_CONTENT: '/api/about_content',

  // Property endpoints
  ALL_PROPERTY: '/api/all_property',
  ALL_AGENT: '/api/all_agent',
  PROPERTY_LIST: '/api/property',
  PROPERTY_DETAILS: (id: string | number) => `/api/property/${id}`,
  /** Property list XLSX export (Angular `property-list.component.ts` export). */
  PROPERTY_EXPORT: '/api/export/property',

  // Invite endpoints
  INVITE_LIST: '/api/invite',
  INVITE_CREATE: '/api/invite',
  INVITE_SEND: (code: string) => `/api/invite/${code}`,
  INVITE_RESEND: (code: string) => `/api/resend-invitation/${code}`,
  INVITE_LOOKUP: '/invite_look_up',
  INVITE_EDIT: (code: string) => `/api/invite/${code}`,
  INVITE_ADD_COAPPLICANT: (code: string) => `/api/invite/${code}/co-applicant`,
  INVITE_LOG: '/api/invite/log',
  INVITE_UPDATE_INVITATION: (code: string) => `/api/update-invitation/${code}`,
  PRE_SCREENING_APPLICATION: '/pre_screening/invite',

  YARDI_GUEST_INVITE_VALIDATE: '/api/yardi_guest_invite/validate',
  YARDI_GUEST_INVITE_SEND: '/api/yardi_guest_invite/send',
  SEARCH_GUEST_CARDS: '/api/search_guest_cards',

  // Apply/Registration endpoints (public, no auth required)
  INVITE_DETAILS: (inviteId: string) => `/invite/${inviteId}`,
  COAPPLICANT_INVITE_DETAILS: (inviteId: string) => `/coapplicant_invite/${inviteId}`,
  INVITE_LOOK_UP_PUBLIC: '/look_up',
  ROLES_CONFIG: '/roles',

  // Organization and Company endpoints
  ALL_ORGANIZATIONS: '/api/all_organizations',
  ALL_COMPANIES: '/api/all_company',

  // Reference data endpoints
  STREETS: '/streets',
  STATES: '/states',

  // Payment endpoints
  PAYMENT_CONFIG: '/api/config/payments',
  PAYMENT_CARDCONNECT: '/api/payment/cardconnect',
  PAYMENT_PAYPAL: (applicantId: string | number) => `/api/resident_application/${applicantId}/application_fee`,
  PAYMENT_RENT: (applicantId: string | number) => `/api/resident_application/${applicantId}/rent_payment`,
  PAYMENT_DEFAULT: (applicantId: string | number) => `/api/resident_application/${applicantId}/default_payment`,
  PAYMENT_CHECK_LAST_ATTEMPT: (applicationId: string | number) => `/api/payment/check_last_payment_attempt/${applicationId}`,
  PAYMENT_INITIALIZE: '/api/payment/initialize_payment',
  APPLICANT_RENT_AMOUNT: (applicantId: string | number) => `/api/applicant_rent_amount/${applicantId}`,
  PAYMENT_LOG_DETAIL: (id: string | number) => `/api/payment_log/${id}`,
  PAYMENT_ADJUSTMENT_DETAIL: (paId: string | number) => `/api/payment_adjustment/${paId}`,
  /** CardConnect status labels (Angular `LoadPaymentResponse` → GET `/gateway_statuses`). */
  GATEWAY_STATUSES: '/gateway_statuses',
  /** Refresh gateway state from the processor (Angular `updateGatewayStatus`). */
  PAYMENT_GATEWAY_STATUS_SYNC: (paymentLogId: string | number) =>
    `/api/payment_gateway_status/${paymentLogId}`,
  UNFREEZE_PAYMENT: (applicantId: string | number) => `/api/unfreeze_payment/${applicantId}`,

  // Bank linking endpoints - Plaid
  PLAID_CREATE_LINK_TOKEN: '/plaid-api/api/create_link_token',
  PLAID_LINK_ACCOUNT: (applicantId: string | number) => `/api/plaid/link/${applicantId}`,
  PLAID_LIST_ACCOUNTS: (applicantId: string | number) => `/api/plaid/list/${applicantId}`,
  PLAID_DELETE_ACCOUNT: (applicantId: string | number, pliId: string | number) => `/api/plaid/list/${applicantId}/${pliId}`,
  PLAID_OPT_OUT: (applicantId: string | number) => `/api/plaid_opt_out/${applicantId}`,

  // Bank linking endpoints - Ninja
  NINJA_CONFIG: '/api/config/ninja',
  /** Security toast content (Angular `checkSecurityNotification`). */
  SECURITY_NOTIFICATION_CONTENT: '/api/config/notification_content',
  NINJA_CREATE_LINK_TOKEN: '/api/ninja/create_link_token',
  NINJA_IBV_REQUEST: (applicantId: string | number) => `/api/ninja/ibv_request/${applicantId}`,
  NINJA_IBV_SESSION_STEPS: (applicantId: string | number) => `/api/ninja/ibv_session_steps/${applicantId}`,
  /** Soft-refresh / resubmit Ninja IBV income report (Angular Resubmit Income Report). */
  NINJA_IBV_SOFT_REFRESH: (applicantId: string | number) => `/api/ninja/ibv_soft_refresh/${applicantId}`,
  /** Manual force-fail bank link so applicant can retry (CS2NEW-1637). */
  NINJA_RESET_BANK_LINK: (applicantId: string | number) =>
    `/api/ninja/reset_bank_link/${applicantId}`,
  /** Bank linking product-request timeline (Angular View Logs). */
  NINJA_APPLICANT_ACCOUNT_REQUESTS: (applicantId: string | number) =>
    `/api/ninja/applicant_account_requests/${applicantId}`,
  NINJA_LINK_ACCOUNT: (applicantId: string | number) => `/api/ninja/link/${applicantId}`,
  NINJA_LIST_ACCOUNTS: (applicantId: string | number) => `/api/ninja/list/${applicantId}`,
  NINJA_DELETE_ACCOUNT: (applicantId: string | number, pliId: string | number) => `/api/plaid/list/${applicantId}/${pliId}`,
  NINJA_OPT_OUT: (applicantId: string | number) => `/api/ninja_opt_out/${applicantId}`,
  NINJA_NO_BANK_UPLOAD: (applicantId: string | number) => `/api/no_bank_ninja/${applicantId}`,

  // Credit bureau config endpoints
  CREDIT_BUREAU_ACTIVE: '/api/config/cb_active',
  CREDIT_BUREAU_DETAILS: (applicantId: string | number) => `/api/config/cb_details/${applicantId}`,

  // User Management endpoints
  USER_LIST: '/api/user',
  USER_DETAIL: (id: string | number) => `/api/user/${id}`,
  USER_UPDATE: (id: string | number) => `/api/user/${id}`,
  USER_DISABLE: (id: string | number) => `/api/user/${id}`,
  USER_DELETE_HARD: (id: string | number) => `/api/user/${id}/delete`,
  USER_BULK_DELETE: '/api/user_delete',
  USER_ENABLE: (id: string | number) => `/api/user/enable/${id}`,
  USER_ROLES_ASSIGN: (id: string | number) => `/api/user/${id}/roles`,
  USER_ROLE_UPDATE: (id: string | number, roleId: string | number) => `/api/user/${id}/roles/${roleId}`,
  USER_ROLE_REMOVE: (id: string | number, roleId: string | number) => `/api/user/${id}/roles/${roleId}`,
  USER_TEMP_PASSWORD: (id: string | number) => `/api/user_password/${id}`,
  USER_EMAIL_CHECK: '/api/email',
  USER_EMAIL_VERIFY: (id: string | number) => `/api/email_verify/${id}`,
  USER_FINANCE_MANAGER: (id: string | number) => `/api/add_finance_manager/${id}`,
  USER_SALES_ROLE: (id: string | number) => `/api/update_sales_team_role/${id}`,
  USER_EXPORT: '/api/export/users',
  /** Product admin (Angular `product-*` components → `/api/product/…`). */
  PRODUCT_LIST: '/api/product',
  PRODUCT_ACTIVE_LIST: '/api/product/active/list',
  PRODUCT_DETAIL: (id: string | number) => `/api/product/${id}`,
  PRODUCT_UPDATE: (id: string | number) => `/api/product/${id}`,
  PRODUCT_CAN_DELETE: (id: string | number) => `/api/product/can_delete_product/${id}`,
  PRODUCT_RISK_MATRIX: (id: string | number) => `/api/product/${id}/risk_matrix`,
  /** Angular CS2NEW-1463 — flat risk labels (replaces matrix on product edit). */
  PRODUCT_RISK_LEVEL: (id: string | number) => `/api/product/${id}/risk_level`,
  PRODUCT_COMM_TEMPLATES: (id: string | number) => `/api/product/${id}/communication_templates`,
  /** Model admin / property model config (Angular `/api/model/…` and `/api/models/…`). */
  MODEL_LIST: '/api/models/',
  MODEL_CREATE: '/api/models',
  MODEL_DETAIL: (id: string | number) => `/api/models/${id}`,
  MODEL_VALIDATE_PKL: (id: string | number) => `/api/models/${id}/validate-pkl`,
  MODEL_CAN_DELETE: (id: string | number) => `/api/models/can_delete_model/${id}`,
  MODEL_PARAM_DEFINITIONS: '/api/models/param-definitions',
  MODEL_ACTIVE_LIST: '/api/model/active/list',
  WORKFLOW_EMAIL_TEMPLATES: (workflowId: string | number) => `/api/workflow/${workflowId}/email_templates`,
  EMAIL_BROADCAST: '/api/email_broadcast',
  SWAP_APPLICANT_INFO: '/api/swap_applicant_info',
  FORGOT_PASSWORD: '/forgotPassword',

  // Password reset (Angular `/auth/resetPassword/:token` flow)
  RESET_PASSWORD: '/resetPassword',
  RESET_PASSWORD_VERIFY: (token: string) => `/resetPassword/${token}`,

  // User Roles Admin endpoints
  USER_ROLES_LIST: '/api/user_roles',
  USER_ROLES_DETAIL: (key: string) => `/api/user_roles/${key}`,
  USER_ROLES_UPDATE: (key: string) => `/api/user_roles/${key}`,
  ALL_POLICY: '/api/all_policy',

  // Notifications endpoints
  USER_NOTIFICATION: '/api/user_notification',
  USER_NOTIFICATION_DETAIL: (id: string | number) => `/api/user_notification/${id}`,
  USER_NOTIFICATION_MULTI_DELETE: (userId: string | number) => `/api/user_notification/multi_delete/${userId}`,
  USER_NOTIFICATION_COUNT: '/api/user_notification_count',
  NOTIFICATION_CENTER: '/api/notification_center',

  // Sales team config (Angular `sales-teams.component.ts`)
  SALES_USERS: '/api/sales_user',
  SALES_TEAM_CONFIG: '/api/config_sales_team',
  COMPANY_ASSIGNMENT_LIST: '/api/company_assignment_list',
  /** Root-level path; proxied like Angular `HttpService.get('/company_city')`. */
  COMPANY_CITY: '/company_city',
  SALES_TEAM_ADD_UPDATE: (id?: string | number) =>
    id ? `/api/add_update_team/${id}` : '/api/add_update_team',
  SALES_TEAM_DELETE: (id: string | number) => `/api/delete_team/${id}`,
  SALES_ROLE_TITLE_UPDATE: '/api/update_sales_role_title',
  SALES_TEAM_ASSIGN: '/api/assign_team',
  SALES_LEAD_UPDATE: (leadId: string | number) => `/api/update_sales_lead/${leadId}`,
} as const

/**
 * Root-level API endpoints that need individual proxy configuration in vite.config.ts
 * These are endpoints that don't start with /api
 */
export const ROOT_LEVEL_ENDPOINTS = [
  '/login',
  '/register',
  '/user',
  '/streets',
  '/states',
  '/city',
  '/refresh-file-token',
  '/invite_look_up',
  '/serve-file',
  '/serve-file-claims',
  '/serve-file-claims-preview',
  /** Policy declaration / access confirmation downloads (`stream_url` from `/policy-declaration-files`) */
  '/stream-file',
  '/serve-file-config',
  '/invite',
  '/coapplicant_invite',
  '/look_up',
  '/roles',
  '/company_city',
  '/pre_screening/invite',
  /** Angular resident editor dropdown source (`GET /default_list`). */
  '/default_list',
  '/forgotPassword',
  '/resetPassword',
  /** Angular `HttpService.get('/gateway_statuses')` — card gateway status display map. */
  '/gateway_statuses',
  /** Angular `loadAndOpenPolicyDeclarationFiles` → `GET /policy-declaration-files/:applicationId` */
  '/policy-declaration-files',
] as const
